import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hasPrivilege, Privileged } from '../../hocs/withPrivileges';
import useAuthentication from '../../hooks/useAuthentication';
import Privileges from '../../utils/Privileges';
import ResetFieldButton from '../buttons/ResetFieldButton';
import SelectField from '../fields/SelectField';
import UserMemberAssignmentTable from './UserMemberAssignmentTable';
import { assignableMembersType, membersType } from '../../types';
import './_styles_/UserMemberAssignment.css';

export const extractRealAssignableMembers = (availableRoles, assignableMembers, assignedRoles) => {
    const allowedMemberTypes = availableRoles.flatMap((availableRole) => {
        let result = null;
        if (assignedRoles.includes(availableRole.role)) {
            result = availableRole.allowedMemberTypes;
        }
        return result;
    });
    const assignableMemberIds = [];
    assignableMembers.forEach((assignableMember) => {
        if (allowedMemberTypes.includes(assignableMember.memberType)) {
            Array.prototype.push.apply(assignableMemberIds, assignableMember.members);
        }
    });
    return Array.from(new Set(assignableMemberIds));
};

UserMemberAssignment.propTypes = {
    assignedMembers: membersType.isRequired,
    assignableMembers: assignableMembersType.isRequired,
    onMemberAssignment: PropTypes.func.isRequired,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
};

UserMemberAssignment.defaultProps = {
    onReset: null,
    isChanged: false,
    isRequired: false,
};

function UserMemberAssignment(props) {
    const {
        assignedMembers,
        assignableMembers,
        onMemberAssignment,
        onReset,
        isChanged,
        isRequired,
    } = props;
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const [memberIdToAssign, setMemberIdToAssign] = useState('');
    useEffect(() => {
        if (!assignableMembers.some((member) => member.memberId === memberIdToAssign)) {
            setMemberIdToAssign('');
        }
    }, [assignableMembers, memberIdToAssign]);
    const addMemberAssignment = () => {
        const memberToBeAssigned = assignableMembers.find((assignableMember) => assignableMember.memberId === memberIdToAssign);
        onMemberAssignment([...assignedMembers, memberToBeAssigned]);
        setMemberIdToAssign('');
    };
    const removeMemberAssignment = (memberId) => () => {
        const newAssignedMembers = assignedMembers.filter((assignedMember) => memberId !== assignedMember.memberId);
        onMemberAssignment(newAssignedMembers);
    };
    const sufficientPrivileges = [Privileges.USER_EDIT];
    const showNoMemberWarning = !assignedMembers || assignedMembers.length === 0;
    return (
        <div className={`users-members field${hasPrivilege(userPrivileges, sufficientPrivileges) ? '' : ' readonly'}${isChanged ? ' modified' : ''}${(isRequired && !assignedMembers.length) ? ' required' : ''}`}>
            <div className="users-members-field">
                <div className="field--key">
                    {t('users.assigned-members')}
                    {showNoMemberWarning && (
                        <div className="validation-warning">
                            {t('users.members.warning.no-member-assigned')}
                        </div>
                    )}
                </div>
                <Privileged sufficientPrivileges={sufficientPrivileges}>
                    <div className="users-members--assign-member">
                        <SelectField
                            label={t('users.members.select-member')}
                            inputId="users-members--fields--member-assign"
                            className="users-members--fields--member-assign"
                            onChange={setMemberIdToAssign}
                            emptyOption={<option value="">...</option>}
                            value={memberIdToAssign}
                            options={assignableMembers
                                .filter((assignableMember) => !assignedMembers.map((assignedMember) => assignedMember.memberId).includes(assignableMember.memberId))
                                .map((assignableMember) => <option key={assignableMember.memberId} value={assignableMember.memberId}>{assignableMember.memberId}</option>)}
                        />
                        <button
                            type="button"
                            className="add-member-assignment-button button button-bordered"
                            disabled={memberIdToAssign === ''}
                            onClick={addMemberAssignment}
                        >
                            <FontAwesomeIcon icon="user-plus" />&nbsp;{t('users.members.assign-new-member')}
                        </button>
                    </div>
                </Privileged>
                {assignedMembers.length > 0 && (
                    <UserMemberAssignmentTable
                        assignedMembers={assignedMembers}
                        assignableMembers={assignableMembers}
                        removeMemberAssignment={removeMemberAssignment}
                    />
                )}
            </div>
            {isChanged && onReset && <ResetFieldButton onReset={onReset} />}
        </div>
    );
}

export default UserMemberAssignment;
